<template>
  <div
    ref="menuRef"
    class="flex w-full flex-col justify-center border-b border-neutral-200 bg-white px-0 py-2 md:relative md:z-10 lg:flex-row lg:py-4"
    @mouseleave="close()"
    @keydown.esc="close()"
  >
    <div class="flex size-full max-w-screen-xl items-center justify-start px-4 pb-1 desktop:px-0">
      <SfButton
        class="block bg-transparent !p-0 font-body text-white hover:!bg-neutral-200 hover:text-white active:!bg-neutral-200 active:text-white desktop:hidden"
        :aria-haspopup="true"
        :aria-expanded="isOpen"
        :aria-label="$t('Open')"
        variant="tertiary"
        square
        @click="toggle()"
      >
        <SfIconMenu class="fill-secondary" />
      </SfButton>
      <NuxtLink
        class="ml-2 mr-auto flex shrink-0 text-white focus-visible:rounded-sm focus-visible:outline focus-visible:outline-offset desktop:ml-0 desktop:mr-4"
        to="/"
        @mouseover="close()"
      >
        <NuxtImg
          class="mr-1 size-11 lg:size-20"
          src="/img/KiK_Logo.svg"
          :alt="$t('HomePage')"
          loading="eager"
          preload
        />
      </NuxtLink>
      <template
        v-for="{ id, name, childrenItems, slug } in categories"
        :key="id"
      >
        <SfButton
          class="!bg-transparent !text-neutral-700 hover:!text-neutral-950 active:!text-neutral-950 mobile:hidden tablet:hidden desktop:flex"
          :aria-haspopup="true"
          :aria-expanded="isOpen"
          variant="tertiary"
          square
          @mouseover="
            open();
            subCategories = childrenItems ?? [];
            categoryId = id;
            categorySlug = slug;
          "
          @focus="
            open();
            subCategories = childrenItems ?? [];
            categoryId = id;
            categorySlug = slug;"
          @click="routeToCategory(slug)"
          @keydown.enter="routeToCategory(slug)"
          @keydown.space="routeToCategory(slug)"
          @keydown.tab="close()"
          @keydown.down.prevent="focusSubCategory"
        >
          <div class="hidden whitespace-nowrap px-2 text-sm md:inline-flex">
            {{ name }}
          </div>
        </SfButton>
      </template>
      <SfButton
        class="hidden !bg-transparent desktop:flex"
        :aria-haspopup="true"
        :aria-expanded="isOpen"
        variant="tertiary"
        square
        @mouseover="close()"
        @focus="close()"
        @click="routeToCategory('s/sale')"
        @keydown.enter="routeToCategory('s/sale')"
        @keydown.space="routeToCategory('s/sale')"
      >
        <div class="hidden whitespace-nowrap px-2 text-sm md:inline-flex">
          {{ $t('Sale') }}
        </div>
      </SfButton>
      <template v-if="categoryConfiguration.topLevelLinks">
        <SharedLink
          v-for="(topLevelLink, index) in categoryConfiguration.topLevelLinks"
          :key="index"
          :link="topLevelLink"
          :data-type="topLevelLink.type"
          class="!bg-transparent p-2 text-sm !text-neutral-700 hover:!text-neutral-950 active:!text-neutral-950 mobile:hidden tablet:hidden"
          :class="{ 'mx-md my-0 min-w-[40px] max-w-[40px] cursor-pointer border-b-0 !p-0': topLevelLink.mode?.type === 'image' }"
          @mouseover="close()"
        >
          <span v-if="topLevelLink.mode?.type === 'title'">
            {{ topLevelLink.mode.title }}
          </span>
          <NuxtImg
            v-if="topLevelLink.mode?.type === 'image' && topLevelLink.mode?.src"
            :src="topLevelLink.mode?.src"
            :placeholder="topLevelLink.mode?.src"
            :alt="topLevelLink.mode?.alt"
            :title="topLevelLink.mode?.title"
            loading="lazy"
          />
        </SharedLink>
      </template>
      <HeaderNavigationMobile
        v-if="$device.isMobileOrTablet"
        ref="navigationRef"
        :is-open="isOpen"
        :categories="categories"
        :category-slug="categorySlug"
        :category-configuration="categoryConfiguration"
        :additional-info="categoryConfiguration"
        @close="close"
      />
      <HeaderNavigation
        v-else
        ref="navigationRef"
        :is-open="isOpen"
        :category-slug="categorySlug"
        :categories="subCategories"
        :images="images"
        tabindex="-1"
        @close="close"
        @focus-next-header-navigation="focusNext"
        @focus-current-header-navigation="focusCurrent"
        @focus-previous-header-navigation="focusPrevious"
      />
      <form
        role="search"
        class="mt-2 hidden flex-[100%] pb-2 desktop:ml-8 desktop:flex"
        :aria-label="$t('Search for items')"
        @submit.prevent="search"
      >
        <SfInput
          ref="searchBarRef"
          v-model="term"
          name="search"
          type="search"
          class="[&::-webkit-search-cancel-button]:appearance-none"
          :placeholder="$t('Search for items')"
          :aria-label="$t('Search for items')"
          wrapper-class="flex-1 h-10 pr-0"
          size="base"
          @input="handleSearch"
          @keydown.enter="handleSearch($event)"
          @keydown.esc="closeSearch"
        >
          <template #suffix>
            <span class="flex items-center">
              <SfButton
                variant="tertiary"
                square
                aria-label="search"
                class="rounded-l-none hover:bg-transparent active:bg-transparent"
                @click="search"
              >
                <SfIconSearch class="fill-secondary" />
              </SfButton>
            </span>
          </template>
        </SfInput>
      </form>
      <nav
        class="flex flex-nowrap items-center justify-end gap-x-2 md:ml-10"
        aria-label="SF Navigation"
      >
        <template v-for="actionItem in actionItems">
          <template v-if="actionItem.type === 'auth'">
            <SfButton
              :key="actionItem.ariaLabel"
              class="relative bg-transparent hover:!bg-neutral-200 active:!bg-neutral-200 mobile:p-1"
              :aria-label="$t(actionItem.ariaLabel)"
              :data-testid="`header__${actionItem.role}-button`"
              variant="tertiary"
              square
              @click="handleAccountClick"
            >
              <template #prefix>
                <Component
                  :is="actionItem.icon"
                  :icon="isAuthenticated ? 'profile_filled' : 'profile'"
                  class="fill-secondary"
                />
              </template>
            </SfButton>
          </template>
          <SfButton
            v-else-if="actionItem.show && actionItem.icon"
            :key="actionItem.ariaLabel"
            class="relative bg-transparent hover:!bg-neutral-200 active:!bg-neutral-200 mobile:p-1"
            :aria-label="$t(actionItem.ariaLabel)"
            :data-testid="`header__${actionItem.role}-button`"
            variant="tertiary"
            square
            @click="actionItem.action"
          >
            <template #prefix>
              <Component
                :is="actionItem.icon"
                :icon="isAuthenticated ? 'profile_filled' : 'profile'"
                class="fill-secondary"
              />
              <SfBadge
                v-if="actionItem.badge && actionItem.badge > 0"
                class="!-right-1.5 !-top-1.5 flex size-[1.4rem] items-center justify-center !rounded-full !bg-primary-700 mobile:size-[1.2rem] mobile:pl-xs"
                :class="{
                  'text-[10px] mobile:text-[9px]': actionItem.badge > 99,
                  'text-sm mobile:text-xs': actionItem.badge <= 99,
                }"
                :content="actionItem.badge"
              />
            </template>
          </SfButton>
          <div
            v-if="actionItem.img"
            :key="actionItem.ariaLabel"
            class="w-12 cursor-pointer"
            :data-testid="`header__${actionItem.role}-button`"
            tabindex="0"
            @click="actionItem.action"
            @keydown.enter="actionItem.action"
            @keydown.space="actionItem.action"
          >
            <NuxtImg
              :alt="actionItem.ariaLabel"
              :src="`/img/topnav/${actionItem.img}`"
              class="m-1.5"
              height="28"
              width="38"
            />
          </div>
        </template>
      </nav>
    </div>
    <form
      role="search"
      class="mx-4 my-2 flex flex-[100%] lg:hidden"
      @submit.prevent="search"
    >
      <SfInput
        v-model="term"
        name="search"
        type="search"
        class="bg-white [&::-webkit-search-cancel-button]:appearance-none"
        :placeholder="$t('Search for items')"
        :aria-label="$t('Search for items')"
        wrapper-class="flex-1 h-10 pr-0"
        size="base"
        @input="handleSearch"
        @keydown.enter="handleSearch($event)"
        @keydown.esc="closeSearch"
      >
        <template #suffix>
          <span class="flex items-center">
            <SfButton
              variant="tertiary"
              square
              aria-label="search"
              type="submit"
              class="rounded-l-none hover:bg-transparent active:bg-transparent"
            >
              <SfIconSearch class="fill-secondary" />
            </SfButton>
          </span>
        </template>
      </SfInput>
    </form>
  </div>
</template>

<script lang="ts" setup>
import {
  SfButton,
  SfIconShoppingCart,
  SfIconFavorite,
  useDisclosure,
  useTrapFocus,
  SfIconMenu,
  SfInput,
  SfIconSearch,
  SfIconLocationOn,
  SfBadge,
} from '@storefront-ui/vue';
import { onClickOutside } from '@vueuse/core';

import { useCart } from '~/composables/custom/useCart/useCart';
import cartGetters from '~/composables/getters/cartGetters';
import { useUser } from '~/composables/custom/useUser/useUser';
import useUiHelpers from '~/composables/useUiHelpers';
import useUiState from '~/composables/useUiState';
import { useFilters } from '~/composables/custom/useFilters';

const { isOpen, toggle, close, open } = useDisclosure();
const $device = useDevice();

const menuRef = ref();
const headerRef = ref();
const navigationRef = ref();
const currentCategory = ref();

interface Props {
  showBasket?: boolean;
  showMyAccount?: boolean;
  showStoreFinder?: boolean;
  categoryConfiguration?: any;
}

const props = withDefaults(defineProps<Props>(), {
  showBasket: true,
  showMyAccount: true,
  showStoreFinder: true,
  categoryConfiguration: undefined,
});

useTrapFocus(headerRef, {
  activeState: isOpen,
  arrowKeysUpDown: true,
  initialFocus: 'container',
});

onClickOutside(menuRef, () => {
  close();
});

const focusSubCategory = (event: KeyboardEvent) => {
  currentCategory.value = event.target as HTMLElement;
  const firstSubCategory = navigationRef.value.$el.querySelector('aside').querySelector('a');
  firstSubCategory?.focus();
};

const focusCurrent = () => {
  currentCategory.value?.focus();
};

const focusNext = () => {
  const nextCategory = currentCategory.value?.nextElementSibling as HTMLElement;
  if (nextCategory) {
    nextCategory.focus();
  }
};

const focusPrevious = () => {
  const previousCategory = currentCategory.value?.previousElementSibling as HTMLElement;
  if (previousCategory) {
    previousCategory.focus();
  }
};

const { getFacetsFromURL } = useUiHelpers();
const { $flags, $i18n } = useNuxtApp();
const router = useRouter();
const { cart } = useCart();
const { isAuthenticated } = useUser();
const { result: categories } = useCategories();
const { wishlistTotalItems } = useWishlist();
const { isMobile } = useDevice();
const { toggleLoginDrawer } = useUiState();
const { removeFilters } = useFilters();

const term = ref<string>(getFacetsFromURL()?.filters?.search?.[0]);
const searchBarRef = ref();

const subCategories = ref<NavbarCategory[]>([]);
const categoryId = ref('');
const categorySlug = ref('');

const images = computed(() => props.categoryConfiguration?.[categoryId.value]?.images || []);

interface ActionItem {
  img?: string;
  icon?: any;
  label: string;
  ariaLabel: string;
  role: string;
  show: boolean;
  action: () => void;
  type?: 'auth';
  badge?: number;
}

const actionItems = computed<ActionItem[]>(() => [
  {
    img: 'myKiK_Vorteilskarte.png',
    label: 'myKiK',
    ariaLabel: 'myKiK',
    role: 'myKiK',
    show: $flags.has('loyalty.program'),
    action: navigateToMyKik,
  },

  {
    icon: SfIconLocationOn,
    label: 'Storefinder',
    ariaLabel: 'Storefinder',
    role: 'storefinder',
    show: props.showStoreFinder,
    action: () => goTo('/filialfinder'),
  },
  {
    icon: resolveComponent('KikIcon'),
    label: 'Log in',
    ariaLabel: 'Log in',
    role: 'login',
    show: props.showMyAccount,
    action: handleAccountClick,
    type: 'auth',
  },
  {
    icon: SfIconFavorite,
    label: '',
    ariaLabel: 'Wishlist',
    role: 'wishlist',
    show: $flags.hasnt('enabledKiosk') && $flags.has('shoppinglist.wishlist'),
    action: () => goTo('/wishlist'),
    badge: wishlistTotalItems.value,
  },
  {
    icon: SfIconShoppingCart,
    label: '',
    ariaLabel: 'Cart',
    role: 'cart',
    show: $flags.has('shop.catalog.enabled') ? false : props.showBasket,
    action: () => goTo('/cart'),
    badge: cartGetters.getCartTotalItems(cart.value),
  },
]);

const handleAccountClick = async () => {
  if (isAuthenticated.value) {
    if (isMobile) {
      return navigateTo('/my-account/');
    }

    return navigateTo(`/my-account/${$i18n.t('Order summary')}`);
  }

  toggleLoginDrawer();
};

const goTo = (to: string) => {
  if (router.currentRoute.value.fullPath !== `${$i18n.t(to)}`) {
    navigateTo(`${$i18n.t(to)}`);
  }
};

const navigateToMyKik = () => navigateTo(isAuthenticated.value ? `/my-account${$i18n.t('MyKikLink')}` : $i18n.t('MyKikContentLink'));

const closeSearch = () => {
  term.value = '';
};

const handleSearch = (paramValue: any) => {
  if (paramValue.target.value === '' || paramValue.target.value === undefined) {
    term.value = '';
  }

  if (!paramValue.target) {
    term.value = paramValue;

    return;
  }

  term.value = paramValue.target.value;
  if (paramValue.keyCode && paramValue.keyCode === 13) {
    removeFilters();
    submitSearch(paramValue.target.value);
  }
};

const submitSearch = (searchTerm: string) => {
  navigateTo(`/search?search=${searchTerm}`);
};

const search = () => {
  if (term.value === '' || term.value === undefined) {
    term.value = '';
  }
  submitSearch(term.value);
};

const routeToCategory = (slug: string) => {
  navigateTo(`/c/${slug}`);
};
</script>
